/**
 * Helpers for recovering from Next.js stale-chunk failures.
 *
 * When a new build is deployed, a client still running the previous page may try
 * to dynamically `import()` JavaScript/CSS chunk filenames that no longer exist on
 * the server (they now 403/404 because the hashes rotated). The webpack chunk
 * loader surfaces this as an unhandled error that lands in an error boundary —
 * commonly `ChunkLoadError` or a `TypeError: Failed to fetch`. The Next.js RSC /
 * server-action runtime surfaces the same stale-chunk failure as
 * `An unexpected response was received from the server.` Reloading the page pulls
 * the current build and recovers transparently.
 */

// Matches the various messages thrown by the webpack chunk loader / dynamic import
// machinery — and the Next.js RSC/server-action runtime — when a chunk can no
// longer be fetched after a deploy.
const CHUNK_LOAD_MESSAGE_PATTERN =
  /Failed to fetch|Loading chunk|Loading CSS chunk|error loading dynamically imported module|Importing a module script failed|An unexpected response was received from the server/i

/** Returns true when the error looks like a post-deploy stale-chunk load failure. */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const name = (error as { name?: unknown }).name
  if (typeof name === 'string' && name === 'ChunkLoadError') return true

  const message = (error as { message?: unknown }).message
  if (typeof message === 'string' && CHUNK_LOAD_MESSAGE_PATTERN.test(message)) return true

  return false
}

// sessionStorage key + window used to guard against reload loops. If the error
// persists after a reload (e.g. it was not actually a stale chunk), we stop after
// one attempt within the window so the user sees the error boundary instead of an
// endless refresh.
const RELOAD_ATTEMPT_KEY = 'chunk-load-error-reload-at'
const RELOAD_GUARD_WINDOW_MS = 10_000

/**
 * If `error` is a stale-chunk failure, trigger a one-time `window.location.reload()`
 * to fetch the current build. Guarded by sessionStorage so a persistent error does
 * not cause a reload loop. Returns true when a reload was triggered.
 */
export function reloadOnChunkLoadError(error: unknown): boolean {
  if (typeof window === 'undefined') return false
  if (!isChunkLoadError(error)) return false

  try {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY))
    if (lastAttempt && Date.now() - lastAttempt < RELOAD_GUARD_WINDOW_MS) {
      // Already reloaded recently and the error came back — avoid a reload loop.
      return false
    }
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(Date.now()))
  } catch {
    // sessionStorage may be unavailable (private mode, blocked storage); fall
    // through and still attempt a single reload.
  }

  window.location.reload()
  return true
}
